import type { Product } from "@/types";

const SITE_NAME = "DrBurns’ ReLeaf";
const SITE_URL = import.meta.env.VITE_SITE_URL || "https://drburnsreleaf.com";

export function pageTitle(title?: string): string {
  return title
    ? `${title} — ${SITE_NAME}`
    : `${SITE_NAME} — Physician-Formulated CBD, Rooted in Science`;
}

export function canonical(path: string): string {
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

// Schema.org Product JSON-LD for a PDP.
export function productJsonLd(product: Product, priceDollars: string) {
  /*
   * DRB-72 — aggregateRating is only emitted when the product actually HAS moderated reviews.
   *
   * These fields used to be published unconditionally from a stored value nothing recomputed, so
   * on 2026-09-01 both live products advertised "9 reviews" to Google while holding 3 and 2. An
   * aggregate that does not correspond to the reviews on the page is a false claim to a customer
   * AND a breach of Google's review-snippet policy. `onReviewWrite` now keeps the stored value
   * true; this guard covers the remaining case, where the honest answer is no rating at all —
   * emitting `reviewCount: 0` is not a smaller claim, it is an invalid one.
   */
  const hasRealRatings =
    Number.isFinite(product.ratingsCount) &&
    product.ratingsCount > 0 &&
    Number.isFinite(product.ratingsAverage) &&
    product.ratingsAverage > 0;

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.shortDescription,
    brand: { "@type": "Brand", name: SITE_NAME },
    image: product.images.map((i) => canonical(i)),
    ...(hasRealRatings
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: product.ratingsAverage,
            reviewCount: product.ratingsCount,
          },
        }
      : {}),
    offers: {
      "@type": "Offer",
      price: priceDollars,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: canonical(`/products/${product.slug}`),
    },
  };
}

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    description:
      "Physician-formulated CBD wellness collection by Dr. Elaine Burns, AACM-Certified.",
  };
}

export function breadcrumbJsonLd(items: Array<{ name: string; path: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: canonical(it.path),
    })),
  };
}
